import React, { useState } from 'react';
import { useVideo } from '../../../contexts/VideoContext';
import axios from 'axios';
import './Steps.css';

// Note: Stripe Elements integration will be added later if needed
// import { loadStripe } from '@stripe/stripe-js';
// const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:10000';

const Step6Payment = () => {
  const { videoData, updateVideoData, prevStep, nextStep } = useVideo();
  const [selectedProduct, setSelectedProduct] = useState('single_video');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [checkingCredits, setCheckingCredits] = useState(false);
  const [availableCredits, setAvailableCredits] = useState(null);

  const products = {
    single_video: {
      name: 'Single Video',
      price: 5.00,
      credits: 1,
      description: 'Perfect for trying it out',
      badge: null,
    },
    video_3pack: {
      name: '3-Video Pack',
      price: 12.00,
      pricePerVideo: 4.00,
      credits: 3,
      description: 'Save $3 - Best for occasional videos',
      badge: 'Popular',
      savings: '$3',
    },
    video_10pack: {
      name: '10-Video Pack',
      price: 35.00,
      pricePerVideo: 3.50,
      credits: 10,
      description: 'Save $15 - Best value for daycares',
      badge: 'Best Value',
      savings: '$15',
    },
  };

  // Check if user already has credits
  React.useEffect(() => {
    const checkExistingCredits = async () => {
      if (!videoData.daycareId) return;

      setCheckingCredits(true);
      try {
        const response = await axios.get(
          `${API_URL}/api/payment/credits/${videoData.daycareId}`
        );

        if (response.data.totalCredits > 0) {
          setAvailableCredits(response.data.totalCredits);
        }
      } catch (err) {
        console.error('Error checking credits:', err);
      } finally {
        setCheckingCredits(false);
      }
    };

    checkExistingCredits();
  }, [videoData.daycareId]);

  const handleProceedWithCredit = () => {
    // User has credits, skip payment and go directly to video generation
    updateVideoData({ paymentStatus: 'has_credits' });
    nextStep();
  };

  const handleCheckout = async () => {
    setLoading(true);
    setError('');

    try {
      // Create checkout session
      const response = await axios.post(
        `${API_URL}/api/payment/create-checkout-session`,
        {
          productType: selectedProduct,
          daycareId: videoData.daycareId,
          customerEmail: videoData.customerEmail || 'customer@example.com',
          metadata: {
            petName: videoData.petName,
            breed: videoData.selectedBreed || 'Custom Photo',
            emotion: videoData.emotion,
          },
        }
      );

      const { sessionId, url } = response.data;

      // Save session ID to context
      updateVideoData({
        stripeSessionId: sessionId,
        selectedProduct,
      });

      // Redirect to Stripe Checkout
      window.location.href = url;
    } catch (err) {
      console.error('Error creating checkout:', err);
      setError(err.response?.data?.error || 'Failed to initiate payment. Please try again.');
      setLoading(false);
    }
  };

  if (checkingCredits) {
    return (
      <div className="wizard-step">
        <div className="step-content">
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Checking your credits...</p>
          </div>
        </div>
      </div>
    );
  }

  // If user has credits, show option to use them
  if (availableCredits > 0) {
    return (
      <div className="wizard-step">
        <div className="step-content">
          <div className="step-icon">💳</div>
          <h2 className="step-title">You Have Credits!</h2>
          <p className="step-description">
            You have {availableCredits} video credit{availableCredits > 1 ? 's' : ''} available.
          </p>

          <div className="credits-banner">
            <div className="credits-info">
              <div className="credits-count">{availableCredits}</div>
              <div className="credits-label">Video{availableCredits > 1 ? 's' : ''} Available</div>
            </div>
          </div>

          <div className="payment-options">
            <button
              type="button"
              className="btn-primary btn-large"
              onClick={handleProceedWithCredit}
            >
              Use My Credit
              <span className="btn-icon">✨</span>
            </button>

            <div className="divider">
              <span>or</span>
            </div>

            <button
              type="button"
              className="btn-secondary btn-large"
              onClick={() => setAvailableCredits(null)}
            >
              Purchase More Credits
            </button>
          </div>

          <div className="step-navigation">
            <button
              type="button"
              className="btn-secondary"
              onClick={prevStep}
            >
              <span className="btn-icon">←</span>
              Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="wizard-step">
      <div className="step-content">
        <div className="step-icon">💳</div>
        <h2 className="step-title">Choose Your Package</h2>
        <p className="step-description">
          Select a package to generate your personalized video
        </p>

        <div className="pricing-cards">
          {Object.entries(products).map(([key, product]) => (
            <div
              key={key}
              className={`pricing-card ${selectedProduct === key ? 'selected' : ''}`}
              onClick={() => setSelectedProduct(key)}
            >
              {product.badge && (
                <div className="pricing-badge">{product.badge}</div>
              )}
              <div className="pricing-header">
                <h3>{product.name}</h3>
                <div className="pricing-amount">
                  <span className="price">${product.price.toFixed(2)}</span>
                  {product.pricePerVideo && (
                    <span className="price-per">${product.pricePerVideo}/video</span>
                  )}
                </div>
              </div>
              <div className="pricing-body">
                <p className="pricing-description">{product.description}</p>
                <ul className="pricing-features">
                  <li>✓ {product.credits} HD Video{product.credits > 1 ? 's' : ''}</li>
                  <li>✓ Custom emotions & activities</li>
                  <li>✓ High-quality lip-sync</li>
                  <li>✓ 9:16 portrait format</li>
                  {product.savings && (
                    <li className="savings">🎉 Save {product.savings}</li>
                  )}
                </ul>
              </div>
              <div className="pricing-select">
                <div className={`radio-indicator ${selectedProduct === key ? 'selected' : ''}`}>
                  {selectedProduct === key && '✓'}
                </div>
              </div>
            </div>
          ))}
        </div>

        {error && <div className="error-message">{error}</div>}

        {/* Navigation */}
        <div className="step-navigation">
          <button
            type="button"
            className="btn-secondary"
            onClick={prevStep}
            disabled={loading}
          >
            <span className="btn-icon">←</span>
            Back
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={handleCheckout}
            disabled={loading}
          >
            {loading ? (
              <>
                <span className="spinner-small"></span>
                Processing...
              </>
            ) : (
              <>
                Proceed to Payment
                <span className="btn-icon">→</span>
              </>
            )}
          </button>
        </div>

        <div className="payment-security">
          <small>
            🔒 Secure payment powered by Stripe. Your payment information is never stored on our servers.
          </small>
        </div>
      </div>
    </div>
  );
};

export default Step6Payment;
